/* Book York Smart Calendar 2.2.3
 * Customer wording revision: clarity-r2 (27 September 2026).
 * Uses the existing public availability, no-hold test-quote, comparison and checkout APIs.
 * Rules are learned from reason_code/rule_value; no minimum stay or price is invented.
 */
(() => {
  'use strict';
  if (window.BookYorkBookingV2) { window.BookYorkBookingV2.initAll(); return; }
  const SELECTOR = '[data-book-york-booking][data-booking-version="2.2.3"]';
  const DAY = 86400000, MAX_NIGHTS = 90, HORIZON = 540, TIMEOUT = 20000;
  const instances = new WeakMap();
  const iso = d => d.toISOString().slice(0, 10);
  const parse = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return null;
    const d = new Date(`${value}T12:00:00Z`);
    return Number.isFinite(d.getTime()) && iso(d) === value ? d : null;
  };
  const add = (value, n) => { const d = parse(value); d.setUTCDate(d.getUTCDate() + n); return iso(d); };
  const nights = (a, b) => Math.round((parse(b) - parse(a)) / DAY);
  const today = () => {
    const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date()).map(x => [x.type, x.value]));
    return `${p.year}-${p.month}-${p.day}`;
  };
  const dateText = (value, full = false) => parse(value)?.toLocaleDateString('en-GB', { day: 'numeric', month: full ? 'long' : 'short', year: 'numeric', ...(full ? { weekday: 'long' } : {}), timeZone: 'UTC' }) || 'Choose date';
  const shortDate = value => parse(value)?.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) || '';
  const monthName = value => parse(value).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const monthStart = value => value.slice(0, 7) + '-01';
  const shiftMonth = (value, amount) => { const d = parse(monthStart(value)); d.setUTCMonth(d.getUTCMonth() + amount); return iso(d); };
  const shiftDateMonth = (value, amount) => { const d = parse(value), day = d.getUTCDate(); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + amount); const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate(); d.setUTCDate(Math.min(day, last)); return iso(d); };
  const nightText = n => `${n} ${n === 1 ? 'night' : 'nights'}`;
  const amount = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
  const money = (n, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));
  const node = (tag, className, value) => { const e = document.createElement(tag); if (className) e.className = className; if (value !== undefined) e.textContent = value; return e; };
  const safeLink = value => { try { const u = new URL(value); return u.protocol === 'https:' ? u.href : ''; } catch { return ''; } };
  const ruleFrom = data => {
    const result = { code: String(data?.reason_code || ''), value: Number(data?.rule_value), message: String(data?.error || '').slice(0, 400) };
    // Older servers may return the same human-readable rule without structured fields.
    if (!result.code) {
      const match = result.message.match(/(?:minimum(?:\s+stay)?(?:\s+of)?|at least)\D{0,15}(\d+)\s*nights?/i);
      if (match) { result.code = 'minimum_stay'; result.value = Number(match[1]); }
    }
    if (!Number.isInteger(result.value) || result.value < 1 || result.value > 550) result.value = null;
    return result;
  };

  function init(root) {
    if (instances.has(root)) return;
    const $ = sel => root.querySelector(sel), $$ = sel => [...root.querySelectorAll(sel)];
    const dialog = $('[data-booking-dialog]'), form = $('[data-booking-form]');
    if (!dialog || !form || !root.dataset.availabilityEndpoint || !root.dataset.quoteEndpoint || !root.dataset.checkoutEndpoint) return;
    const life = new AbortController();
    const listen = (el, type, fn, opts = {}) => el?.addEventListener(type, fn, { ...opts, signal: life.signal });
    const slots = {};
    let disposed = false, opener = null, oldOverflow = '', scrollLocked = false, observer;
    let availability = null, availabilityAt = 0, displayMonth = monthStart(today());
    let pendingView = 'arrival', focusedDate = '', discountTimer;
    let restrictedDepartures = new Set();
    const from = today(), to = add(from, HORIZON);
    const configuredCapacity = Number(root.dataset.maxGuests);
    const capacity = Number.isInteger(configuredCapacity) && configuredCapacity > 0 ? configuredCapacity : null;
    const state = { confirmed: false, adults: 1, children: 0, draftAdults: 1, draftChildren: 0, arrival: '', departure: '', view: 'guests', quote: null, payload: null, busy: false, checkoutBusy: false };
    let rules = { status: 'idle', minimum: null, earliest: '', required: null, context: '', message: '', code: '' };
    const emit = (name, detail = {}) => root.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    const abort = name => { if (slots[name]) { slots[name].controller.abort(); slots[name] = null; } };
    const task = name => { abort(name); const t = { controller: new AbortController(), timedOut: false }; slots[name] = t; return t; };
    const current = (name, t) => !disposed && root.isConnected && slots[name] === t;
    const fetchJSON = async (url, body, t) => {
      const timer = window.setTimeout(() => { t.timedOut = true; t.controller.abort(); }, TIMEOUT);
      try {
        const response = await fetch(url, { method: body ? 'POST' : 'GET', credentials: 'omit', headers: body ? { 'content-type': 'application/json' } : {}, ...(body ? { body: JSON.stringify(body) } : {}), signal: t.controller.signal });
        const data = await response.json().catch(() => null);
        if (!response.ok || data?.ok !== true) {
          const e = new Error(data?.error || (response.status === 429 ? 'Too many checks at once. Please pause, then try again.' : 'We could not complete that check. Please try again.'));
          e.data = data; e.status = response.status; throw e;
        }
        return data;
      } catch (e) {
        if (t.timedOut) throw new Error('The booking service took too long to reply. Your choices are saved; please try again.');
        throw e;
      } finally { window.clearTimeout(timer); }
    };
    const party = () => `${state.adults} ${state.adults === 1 ? 'adult' : 'adults'}${state.children ? ` · ${state.children} ${state.children === 1 ? 'child' : 'children'}` : ''}`;
    const discount = () => String($('[data-discount-code]')?.value || '').trim();
    const payloadFor = (end, useDiscount = true) => ({ arrival: state.arrival, departure: end, adults: state.adults, children: state.children, pets: 0, ...(useDiscount && discount() ? { discount_code: discount() } : {}) });
    const fingerprint = p => p ? JSON.stringify([p.arrival, p.departure, p.adults, p.children, p.discount_code || '']) : '';
    const setError = (message = '') => { const e = $('[data-booking-error]'); e.textContent = message; e.hidden = !message; };
    const status = (message, tone = '') => { const e = $('[data-booking-auto-status]'); e.textContent = message; e.dataset.state = tone; };
    const invalidateQuote = () => {
      const had = !!state.quote;
      abort('quote'); abort('comparison'); clearTimeout(discountTimer);
      state.quote = null; state.payload = null; state.busy = false;
      $('[data-booking-quote]').hidden = true;
      $('[data-booking-review-notice]').hidden = true;
      $('[data-booking-continue]').disabled = true;
      $('[data-booking-retry]').hidden = true;
      root.removeAttribute('aria-busy');
      if (had) emit('bookyork:booking-reset');
    };
    const resetRules = () => { abort('rules'); restrictedDepartures.clear(); rules = { status: 'idle', minimum: null, earliest: '', required: null, context: '', message: '', code: '' }; };
    const updateSummary = () => {
      form.elements.adults.value = String(state.adults); form.elements.children.value = String(state.children); form.elements.guests_selected.value = state.confirmed ? '1' : '0';
      $('[data-guest-summary]').textContent = state.confirmed ? party() : 'Select guests';
      $('[data-arrival-label]').textContent = dateText(state.arrival);
      $('[data-departure-label]').textContent = dateText(state.departure);
      $('[data-dialog-guests]').textContent = state.confirmed ? `${state.adults + state.children} ${(state.adults + state.children) === 1 ? 'guest' : 'guests'}` : 'Select guests';
      $('[data-dialog-arrival]').textContent = dateText(state.arrival);
      $('[data-dialog-departure]').textContent = dateText(state.departure);
      $('[data-guest-trigger]').classList.toggle('has-value', state.confirmed);
      $('[data-guest-trigger] small').textContent = state.confirmed ? 'Guests' : 'Guests · start here';
      $('.byb-minimum-note').hidden = !!state.quote;
      $$('[data-open-calendar]').forEach(b => {
        const value = state[b.dataset.openCalendar];
        b.classList.toggle('has-value', !!value);
        b.setAttribute('aria-label', `${b.dataset.openCalendar === 'arrival' ? 'Check-in' : 'Check-out'}${value ? `, ${dateText(value)}` : ', choose date'}${!state.confirmed ? '. First confirm your guests.' : ''}`);
      });
      const active = !state.confirmed ? 'guests' : state.quote ? 'price' : 'dates';
      $$('[data-progress]').forEach(li => { if (li.dataset.progress === active) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current'); li.classList.toggle('is-complete', (li.dataset.progress === 'guests' && state.confirmed) || (li.dataset.progress === 'dates' && !!state.quote)); });
      const start = $('[data-booking-start]');
      start.hidden = !!state.quote;
      start.textContent = !state.confirmed ? 'Choose guests & dates' : !state.arrival ? 'Choose dates' : !state.departure ? 'Choose check-out' : state.busy ? 'Checking your price…' : 'See price';
      $$('[data-edit-guests], [data-edit-arrival], [data-edit-departure]').forEach(b => b.classList.toggle('is-active', b.hasAttribute(`data-edit-${state.view === 'guests' ? 'guests' : state.view}`)));
      $('[data-clear-dates]').hidden = !state.arrival || state.view === 'guests';
      const stay = $('[data-stay-summary]');
      stay.textContent = state.departure ? `${shortDate(state.arrival)} – ${shortDate(state.departure)} · ${nightText(nights(state.arrival, state.departure))}` : '';
      stay.hidden = !state.departure;
      $('.byb-footer-meta').hidden = state.view === 'guests' || !state.arrival;
    };
    const updateDraft = () => {
      $('[data-guest-count="adults"]').textContent = state.draftAdults;
      $('[data-guest-count="children"]').textContent = state.draftChildren;
      const total = state.draftAdults + state.draftChildren;
      $$('[data-guest-step]').forEach(b => { const adults = b.dataset.guestStep === 'adults', increase = b.dataset.direction === '1'; const n = adults ? state.draftAdults : state.draftChildren; b.disabled = increase ? (capacity ? total >= capacity : n >= (adults ? 10 : 8)) : n <= (adults ? 1 : 0); });
      $('[data-guest-note]').textContent = capacity ? `Up to ${capacity} guests in total, including children. At least one adult is required.` : 'Include every guest, including infants. At least one adult is required.';
    };
    const showGuidance = (title, copy, tone = 'neutral') => {
      const box = $('[data-guidance]'); box.dataset.tone = tone;
      $('[data-guidance-title]').textContent = title;
      $('[data-guidance-copy]').textContent = copy;
    };
    const updateGuidance = () => {
      $('[data-rule-retry]').hidden = true;
      $('[data-rule-guests]').hidden = true;
      $('[data-rule-arrival]').hidden = true;
      if (!availability) {
        const loading = !!slots.availability;
        showGuidance(loading ? 'Loading your dates…' : 'The calendar did not load', loading ? '' : 'A connection problem, not a lack of availability. Please try again.', loading ? 'loading' : 'notice');
      } else if (state.busy) {
        showGuidance('Checking your price…', 'Your dates are saved while we check this stay.', 'loading');
      } else if (state.view === 'arrival') {
        showGuidance('Choose your check-in date', 'Minimum stays vary by arrival date.');
      } else if (rules.status === 'blocked') {
        showGuidance(rules.minimum ? `Minimum stay: ${nightText(rules.minimum)}` : 'Please change your dates', rules.message || 'A booked night interrupts this stay. Try a different check-in.', 'notice');
        $('[data-rule-arrival]').hidden = false;
      } else if (rules.status === 'error') {
        const occupancy = rules.code === 'occupancy';
        showGuidance(occupancy ? 'Please adjust your guests' : 'Please check your selection', rules.message || 'We could not check the minimum. You can retry or choose a check-out for a full check.', 'notice');
        $('[data-rule-retry]').hidden = occupancy || !!state.departure;
        $('[data-rule-guests]').hidden = !occupancy;
      } else if (state.departure) {
        showGuidance('Your dates are selected', 'Select See price to check the total for your stay.');
      } else if (rules.status === 'loading') {
        showGuidance(rules.minimum ? `Minimum stay: ${nightText(rules.minimum)}` : 'Checking the minimum stay…', rules.context || 'Choose your check-out date.', 'loading');
      } else if (rules.context) {
        // An interval-specific minimum must not disable a shorter, valid stay.
        showGuidance('Choose another check-out', `${rules.context} Try a longer stay, or choose a shorter interval.`, 'notice');
      } else if (rules.minimum) {
        const earliest = rules.earliest || add(state.arrival, rules.minimum);
        showGuidance('Choose your check-out date', `Minimum stay: ${nightText(rules.minimum)} · From ${shortDate(earliest)}.`);
      } else {
        showGuidance('Choose your check-out date', 'Your full stay and price will be checked next.');
      }
      $('[data-guidance-actions]').hidden = !['[data-rule-retry]', '[data-rule-guests]', '[data-rule-arrival]'].some(sel => !$(sel).hidden);
      updateFooter();
    };
    const updateFooter = () => {
      const primary = $('[data-guest-done]');
      primary.disabled = false;
      if (state.view === 'guests') {
        primary.textContent = 'Choose dates';
      } else if (state.busy) {
        primary.textContent = 'Checking price…'; primary.disabled = true;
      } else if (!availability) {
        primary.textContent = slots.availability ? 'Loading dates…' : 'Retry calendar';
        primary.disabled = !!slots.availability;
      } else {
        // No departure is ever chosen by this button. Both dates must be deliberate.
        primary.textContent = 'See price';
        primary.disabled = state.view !== 'departure' || !state.arrival || !state.departure || !!dayReason(state.departure);
      }
      $('[data-dialog-back]').hidden = true; // The fields themselves are the edit controls.
    };
    const setView = value => {
      state.view = value;
      dialog.dataset.view = value;
      $('[data-guest-panel]').hidden = value !== 'guests';
      $('[data-booking-calendar]').hidden = value === 'guests';
      $('[data-dialog-summary]').hidden = value === 'guests';
      $('[data-edit-guests]').hidden = value === 'guests';
      $('[data-dialog-step]').textContent = value === 'guests' ? 'Step 1 of 3 · Guests' : 'Step 2 of 3 · Dates';
      $('[data-dialog-title]').textContent = value === 'guests' ? 'Choose guests' : 'Choose dates';
      $('[data-dialog-intro]').textContent = value === 'guests' ? 'Include everyone staying so we can show the correct price.' : 'Choose your check-in, then your check-out, then select See price.';
      $('[data-dialog-intro]').hidden = value !== 'guests';
      updateSummary(); updateDraft(); updateFooter();
      if (value !== 'guests') { renderCalendar(); updateGuidance(); }
      $('.byb-dialog-scroll').scrollTop = 0;
    };
    const rangeBlocked = (a, b) => { if (!availability) return null; for (let d = a; d < b; d = add(d, 1)) if (availability.has(d)) return d; return ''; };
    const dayReason = d => {
      if (d < today()) return 'past';
      if (d > to || (state.view === 'arrival' && d === to)) return 'outside';
      if (!availability) return 'loading';
      if (state.view === 'arrival') return availability.has(d) ? 'booked' : '';
      if (d <= state.arrival) return 'before';
      if (nights(state.arrival, d) > MAX_NIGHTS) return 'long';
      if (restrictedDepartures.has(d)) return 'restricted';
      if (rangeBlocked(state.arrival, d)) return 'blocked';
      if (rules.minimum && nights(state.arrival, d) < rules.minimum) return 'short';
      return '';
    };
    const reasonText = (reason, d) => {
      if (reason === 'blocked') {
        const blocked = state.arrival ? rangeBlocked(state.arrival,d) : '';
        return `A booked night${blocked ? ` on ${shortDate(blocked)}` : ''} interrupts this stay. Choose an earlier departure or a different arrival.`;
      }
      if (reason === 'short') {
        const more = rules.minimum - nights(state.arrival,d);
        return `Stay at least ${nightText(rules.minimum)} from this arrival. ${nightText(more)} more ${more === 1 ? 'is' : 'are'} needed. Choose a later check-out date.`;
      }
      return {
        past: 'This date is in the past.',
        outside: 'This date is outside the calendar shown. Please contact us for later dates.',
        booked: 'This night is booked. Choose an open night to arrive.',
        before: 'Check-out must be after your arrival. Use the Check-in field above to change arrival.',
        long: 'Please contact us for stays longer than 90 nights.',
        restricted: 'Check-out is not permitted on this date. Choose another check-out date.',
        loading: 'Please load the calendar first.'
      }[reason] || '';
    };

    function renderCalendar(focusTarget = '') {
      const grid = $('[data-calendar-grid]');
      const oldFocus = grid.contains(document.activeElement) ? document.activeElement.dataset.date : '';
      const wanted = focusTarget || oldFocus || focusedDate || (state.view === 'departure' ? state.departure || state.arrival : state.arrival || today());
      const mobile = window.matchMedia('(max-width: 719px)').matches;
      grid.replaceChildren();
      if (!availability) { grid.hidden = true; return; }
      grid.hidden = false;
      const headings = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
      const visibleDates = [];
      for (let m = 0; m < (mobile ? 1 : 2); m++) {
        const month = shiftMonth(displayMonth, m);
        if (month > monthStart(to)) break;
        const block = node('section','byb-month');
        const h = node('h4','',monthName(month)); h.id = `${dialog.id}-month-${month}`; block.append(h);
        const table = node('table', 'byb-calendar-table'); table.setAttribute('role','grid'); table.setAttribute('aria-label',`${monthName(month)}. Choose ${state.view === 'arrival' ? 'check-in' : 'check-out'}.`);
        const thead = node('thead'), hr = node('tr');
        headings.forEach(day => { const th = node('th','',day.slice(0,3)); th.scope = 'col'; th.setAttribute('abbr', day); hr.append(th); });
        thead.append(hr); table.append(thead);
        const body = node('tbody');
        const startOffset = (parse(month).getUTCDay() + 6) % 7;
        const days = nights(month, shiftMonth(month, 1));
        for (let week = 0; week < Math.ceil((startOffset + days)/7); week++) {
          const row = node('tr');
          for (let w = 0; w < 7; w++) {
            const day = week * 7 + w - startOffset + 1;
            const td = node('td'); td.setAttribute('role','gridcell');
            if (day > 0 && day <= days) {
              const d = add(month, day - 1), reason = dayReason(d);
              const b = node('button','byb-day'); b.type = 'button'; b.dataset.date = d; b.dataset.reason = reason;
              const selected = d === state.arrival || d === state.departure;
              if (selected) { b.classList.add('is-selected'); td.setAttribute('aria-selected','true'); }
              if (reason) { b.setAttribute('aria-disabled','true'); b.classList.add(`is-${reason === 'before' ? 'blocked' : reason}`); }
              if (state.arrival && state.departure && d > state.arrival && d < state.departure) b.classList.add('is-range');
              // Only the visitor's actual arrival and departure receive selection styling.
              if (d === today()) b.setAttribute('aria-current','date');
              b.append(node('span','',String(day)));
              // Night counts belong in the chosen-stay summary, not in every date cell.
              let label = `${dateText(d,true)}. `;
              label += d === state.arrival ? 'Selected check-in. ' : d === state.departure ? 'Selected check-out. ' : '';
              label += reason ? reasonText(reason,d) : state.view === 'arrival' ? 'Open night; check stay rules by selecting this arrival.' : `${nightText(nights(state.arrival,d))}. Choose this check-out date.`;
              b.setAttribute('aria-label',label); b.tabIndex = -1; td.append(b); visibleDates.push(b);
            }
            row.append(td);
          }
          body.append(row);
        }
        table.append(body); block.append(table); grid.append(block);
      }
      const chosen = visibleDates.find(b => b.dataset.date === wanted) || visibleDates.find(b => !b.dataset.reason) || visibleDates[0];
      if (chosen) { chosen.tabIndex = 0; focusedDate = chosen.dataset.date; if (focusTarget || oldFocus) chosen.focus({preventScroll:!focusTarget}); }
      $('[data-month-jump]').value = displayMonth;
      const announcement = $('[data-calendar-announcement]');
      const monthLabel = mobile ? monthName(displayMonth) : `${monthName(displayMonth)} and ${monthName(shiftMonth(displayMonth, 1))}`;
      if (announcement.textContent !== monthLabel) announcement.textContent = monthLabel;
      $('[data-calendar-prev]').disabled = displayMonth <= monthStart(from);
      $('[data-calendar-next]').disabled = displayMonth >= monthStart(to);
      $('[data-short-key]').hidden = !(state.view === 'departure' && rules.minimum > 1);
      $('[data-open-key]').textContent = state.view === 'departure' ? 'Available check-out' : 'Available';
      $('[data-booked-key]').hidden = state.view === 'departure';
      $('[data-blocked-key]').hidden = state.view !== 'departure';
    }
    const populateMonths = () => {
      const select = $('[data-month-jump]'); select.replaceChildren();
      for (let m = monthStart(from); m <= monthStart(to); m = shiftMonth(m, 1)) { const option = node('option','',monthName(m)); option.value = m; select.append(option); }
    };
    const ensureAvailability = async (force = false) => {
      if (availability && !force && Date.now() - availabilityAt < 120000) { renderCalendar(); updateGuidance(); return true; }
      if (slots.availability && !force) return false;
      const t = task('availability');
      if (force) availability = null;
      $('[data-calendar-loading]').hidden = false; $('[data-availability-retry]').hidden = true; renderCalendar(); updateGuidance();
      try {
        const url = new URL(root.dataset.availabilityEndpoint); url.searchParams.set('from',from); url.searchParams.set('to',to);
        const data = await fetchJSON(url.toString(),null,t);
        if (!current('availability',t)) return false;
        if (!Array.isArray(data.unavailable) || data.unavailable.some(d => !parse(d))) throw new Error('The calendar response was incomplete. Please try again.');
        if ((data.from && data.from > from) || (data.to && data.to < to)) throw new Error('The calendar response did not cover the requested dates. Please try again.');
        availability = new Set(data.unavailable); availabilityAt = Date.now();
        renderCalendar();
        slots.availability = null;
        updateGuidance(); return true;
      } catch (e) {
        if (!current('availability',t)) return false;
        availability = null; slots.availability = null;
        $('[data-availability-retry]').hidden = false;
        updateGuidance(); return false;
      } finally { if (!slots.availability || slots.availability === t) $('[data-calendar-loading]').hidden = true; }
    };
    const inRange = d => parse(d) && d >= today() && d <= to;
    const visibleMonthFor = d => { if (inRange(d)) displayMonth = monthStart(d); };
    const unlockScroll = () => { if (scrollLocked) { document.body.style.overflow = oldOverflow; scrollLocked = false; } };
    const closeDialog = (focus = true) => {
      abort('rules'); if (rules.status === 'loading') rules.status = 'idle';
      if (dialog.open) dialog.close();
      unlockScroll();
      $$('[aria-controls]').forEach(e => { if (e.getAttribute('aria-controls') === dialog.id) e.setAttribute('aria-expanded','false'); });
      if (focus && opener?.isConnected) opener.focus({preventScroll:true});
    };
    const open = async (requested = 'arrival', trigger = null) => {
      if (state.checkoutBusy) return;
      if (state.busy) {
        invalidateQuote();
        status('Your choices are saved. Select See price when ready.', 'waiting');
      }
      pendingView = requested === 'guests' ? (state.arrival ? 'departure' : 'arrival') : requested;
      const v = !state.confirmed || requested === 'guests' ? 'guests' : requested === 'departure' && !state.arrival ? 'arrival' : requested;
      if (trigger && !dialog.contains(trigger)) opener = trigger;
      if (!opener) opener = $('[data-booking-start]');
      if (v === 'guests') { abort('rules'); state.draftAdults = state.adults; state.draftChildren = state.children; }
      else if (v === 'arrival' && state.arrival) visibleMonthFor(state.arrival);
      else if (v === 'departure' && state.arrival) visibleMonthFor(state.departure || state.arrival);
      setView(v);
      if (!dialog.open) { oldOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; scrollLocked = true; dialog.showModal(); }
      if (trigger?.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded','true');
      $('[data-dialog-title]').focus({preventScroll:true});
      if (v !== 'guests') {
        const ready = await ensureAvailability();
        if (ready && dialog.open && state.view === 'departure' && state.arrival && !state.busy) {
          if (rules.status === 'ready' && Date.now() - (rules.checkedAt || 0) > 60000) resetRules();
          if (['idle','loading'].includes(rules.status)) checkRules();
        }
      }
    };

    async function checkRules({ startAt = 1, context = '', learnBase = true } = {}) {
      if (!state.confirmed || !state.arrival || !availability || state.busy) return;
      const t = task('rules'); const a = state.arrival;
      const snapshot = `${a}/${state.adults}/${state.children}`;
      rules = { ...rules, status: 'loading', earliest: '', context, required: learnBase ? null : startAt, code: '', message: '' };
      updateGuidance();
      let length = startAt;
      // Bounded no-hold test quotes. Never brute-force a month or create a checkout quote.
      for (let attempt = 0; attempt < 4; attempt++) {
        const end = add(a,length);
        if (length > MAX_NIGHTS || end > to || rangeBlocked(a,end)) {
          if (current('rules',t)) { slots.rules = null; rules.status = 'blocked'; rules.message = length > MAX_NIGHTS ? 'Please contact us to arrange a stay longer than 90 nights.' : end > to ? 'This stay extends beyond the calendar shown. Please contact us for these dates.' : `A booked night interrupts the ${nightText(length)} needed for this option. Change your check-in date; other dates in this month may still work.`; updateGuidance(); renderCalendar(); }
          return;
        }
        try {
          await fetchJSON(root.dataset.quoteEndpoint, payloadFor(end,false), t);
          if (!current('rules',t) || snapshot !== `${state.arrival}/${state.adults}/${state.children}`) return;
          rules.status = 'ready'; rules.earliest = end; rules.checkedAt = Date.now();
          if (learnBase && rules.minimum === null && length === 1) rules.minimum = 1;
          slots.rules = null;
          updateGuidance(); renderCalendar(); return;
        } catch (e) {
          if (!current('rules',t)) return;
          const r = ruleFrom(e.data);
          if (r.code === 'minimum_stay' && r.value && r.value > length) {
            length = r.value;
            if (learnBase) rules.minimum = r.value; else rules.required = r.value;
            updateGuidance(); renderCalendar(); continue;
          }
          if (r.code === 'departure_restricted') { restrictedDepartures.add(end); length++; continue; }
          rules.status = ['unavailable','arrival_restricted','maximum_stay'].includes(r.code) ? 'blocked' : 'error';
          rules.code = r.code;
          rules.message = r.code === 'occupancy' ? 'This party size is not accepted for this property. Adjust your guests above; your dates will be kept.' : r.code === 'arrival_restricted' ? 'Check-in is not permitted on this date. Choose a different arrival; this does not mean the month is fully booked.' : r.code === 'unavailable' ? 'That stay was not available when checked. Try another departure or change your arrival.' : r.code === 'maximum_stay' ? r.message : 'The minimum-stay check could not be completed. Your arrival is saved. Try again, or choose a departure for a full check.';
          slots.rules = null; updateGuidance(); renderCalendar(); return;
        }
      }
      if (current('rules',t)) { slots.rules = null; rules.status = 'error'; rules.message = 'Choose a check-out date for a full stay check, or try a different arrival.'; updateGuidance(); renderCalendar(); }
    }

    async function chooseDate(d) {
      if (!state.confirmed) { open('guests'); return; }
      const reason = dayReason(d);
      if (reason) { showGuidance(reason === 'short' ? `Minimum stay: ${nightText(rules.minimum)}` : 'Choose a different date',reasonText(reason,d),'notice'); return; }
      if (state.view === 'arrival') {
        invalidateQuote(); resetRules(); setError('');
        state.arrival = d; state.departure = ''; focusedDate = d;
        setView('departure'); status('Check-in selected. Now choose your check-out date.');
        checkRules();
      } else {
        abort('rules'); if (rules.status === 'loading') rules.status = 'idle';
        invalidateQuote(); setError(''); state.departure = d;
        rules.context = ''; rules.message = ''; rules.code = '';
        if (rules.status === 'error') rules.status = 'idle';
        updateSummary(); renderCalendar(); updateGuidance();
        status('Your dates are selected. Select See price to check your total.');
      }
    }

    function cancellationPresentation(text, data, payload, paymentValid) {
      const clean = String(text || '').replace(/\s+/g, ' ').trim();
      // Only translate the complete, observed Hunter House policy. A different rate,
      // cutoff, grace period, fee or wording must keep its own source text visible.
      // In particular, "more than 30" must never become "at least 30".
      const observedPolicy = /^Eighty percent \(80(?:\.0+)?%\) of the total booking charges will be refunded if the cancellation is made more than 30 days prior to the arrival date of the reservation\. For cancellations made within 30 days prior to the arrival date, no refund will be issued\.$/i;
      if (String(root.dataset.propertyId) !== '313086' || !observedPolicy.test(clean)) return null;

      const terms = data.booking_terms;
      const arrival = parse(payload?.arrival);
      const effective = parse(terms?.effective_date);
      const datesKnown = arrival && effective && terms.time_zone === 'Europe/London' && payload.arrival >= terms.effective_date;
      const withinCutoff = datesKnown && nights(terms.effective_date, payload.arrival) <= 30;
      // Calculate the cancellation date from this policy, NOT from a payment deadline.
      const cutoff = arrival ? parse(add(payload.arrival, -30)).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : '';
      const retained = amount(data.total) && Number(data.total) >= 0 ? Math.round(Math.round(Number(data.total) * 100) / 5) / 100 : null;
      const retainedText = retained === null ? '' : ` (${money(retained, data.currency || 'GBP')})`;
      const depositMatches = paymentValid && retained !== null && retained > 0 &&
        Math.abs(Number(terms.payment.due_now) - retained) < .005 && terms.payment.instalments.length > 0;
      const explanation = 'If you cancel in the earlier period and have already paid more than 20% of the stay total, the excess is refundable. If you have paid only the 20% booking payment, it is retained and the remaining balance is not due.';
      if (withinCutoff) {
        return { paragraphs: [{ heading: '', text: 'Your arrival is within 30 days. The full stay price is non-refundable.' }], explanation };
      }
      return {
        paragraphs: [
          {
            heading: cutoff ? `Cancel before ${cutoff}` : 'More than 30 days before arrival',
            text: depositMatches
              ? `Your 20% payment${retainedText} is non-refundable. No further balance is due.`
              : `20% of the stay total${retainedText} is non-refundable. Any amount paid above this is refundable.`
          },
          { heading: cutoff ? `From ${cutoff}` : '30 days or less before arrival', text: 'The full stay price is non-refundable.' }
        ],
        explanation
      };
    }

    function renderBookingTerms(data, payload) {
      const terms = data.booking_terms?.schema_version === 1 ? data.booking_terms : null;
      const currency = data.currency || 'GBP';
      const paymentBox = $('[data-payment-summary]'), cancelBox = $('[data-cancellation-summary]');
      paymentBox.replaceChildren(); cancelBox.replaceChildren();
      const p = terms?.payment;
      const sourceNote = $('.byb-terms-note');
      if (sourceNote) sourceNote.textContent = 'You’ll review the full terms again at secure checkout before payment.';
      const later = Array.isArray(p?.instalments) ? p.instalments : [];
      const valid = p?.status === 'ready' && amount(p.due_now) && Number(p.due_now) >= 0 && later.length <= 12 &&
        (!terms.currency || terms.currency === currency) && later.every(i => amount(i.amount) && Number(i.amount) >= 0 && parse(i.due_date)) &&
        Math.abs(Number(p.due_now) + later.reduce((n,i) => n + Number(i.amount),0) - Number(data.total)) < .011;
      if (valid) {
        const row = node('div','byb-payment-row');
        row.append(node('strong','',!later.length ? 'Full payment when you book' : 'When you book'), node('strong','byb-payment-amount',money(p.due_now,currency)));
        paymentBox.append(row);
        later.forEach(i => {
          const entry = node('div','byb-payment-row');
          const label = node('span','',i.automatic === true ? 'Collected automatically' : 'Balance due');
          label.append(node('span','byb-payment-date',`on ${parse(i.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric',timeZone:'UTC'})}`));
          entry.append(label, node('strong','byb-payment-amount',money(i.amount,currency)));
          paymentBox.append(entry);
        });
      } else {
        paymentBox.append(node('p','byb-terms-unavailable','Your payment schedule will be confirmed at secure checkout before you pay. We could not retrieve it here.'));
      }
      const cancellation = terms?.cancellation;
      const rawText = cancellation?.status === 'ready' && typeof cancellation.text === 'string' ? cancellation.text.trim() : '';
      const presentation = cancellationPresentation(rawText, data, payload, valid);
      cancelBox.append(node('strong','','Cancellation'));
      const detail = $('[data-cancellation-details]');
      detail.open = false;
      detail.hidden = !presentation;
      // Never inject upstream policy text as HTML or silently replace unknown policies.
      $('[data-cancellation-text]').textContent = rawText;
      detail.querySelector('[data-cancellation-explanation]')?.remove();
      if (presentation) {
        presentation.paragraphs.forEach(({ heading, text }) => {
          const paragraph = node('p');
          if (heading) paragraph.append(node('strong', '', heading), node('br'));
          paragraph.append(document.createTextNode(text));
          cancelBox.append(paragraph);
        });
        const explanation = node('p', 'byb-policy-text', presentation.explanation);
        explanation.setAttribute('data-cancellation-explanation', '');
        detail.append(explanation);
      } else {
        // An unrecognised policy remains fully visible, irrespective of its length.
        cancelBox.append(node('p','',rawText || 'The applicable cancellation terms will be confirmed at secure checkout before you pay. We could not retrieve them here.'));
      }
    }

    function renderQuote(data, payload) {
      const currency = data.currency || 'GBP';
      const top = node('div','book-york-booking__quote-top');
      top.append(node('span','book-york-booking__quote-kicker',`${nightText(nights(payload.arrival,payload.departure))} · ${payload.adults + payload.children} ${(payload.adults + payload.children) === 1 ? 'guest' : 'guests'} · total stay price`));
      top.append(node('strong','book-york-booking__total',money(data.total,currency)));
      top.append(node('span','book-york-booking__quote-dates',`${dateText(payload.arrival)} – ${dateText(payload.departure)}`));
      if (payload.discount_code) top.append(node('span','book-york-booking__discount-checked',`Discount code ${payload.discount_code} included in this live quote`));
      const overview = node('div','book-york-booking__price-overview');
      [['Accommodation',data.base_rent],['Fees & taxes',data.fees_taxes],['Total',data.total]].forEach(([label,value]) => { if (!amount(value)) return; const row = node('div',label === 'Total' ? 'is-total' : ''); row.append(node('span','',label),node('strong','',money(value,currency))); overview.append(row); });
      $('[data-booking-quote-summary]').replaceChildren(top,overview);
      const rows = $('[data-booking-breakdown-rows]'); rows.replaceChildren();
      (Array.isArray(data.charges) ? data.charges : []).filter(c => amount(c.amount)).forEach(c => { const row = node('div','book-york-booking__breakdown-row'); row.append(node('span','',c.description || 'Stay charge'),node('strong','',money(c.amount,currency))); rows.append(row); });
      $('[data-booking-breakdown]').hidden = !rows.children.length;
      renderBookingTerms(data, payload);
      $('[data-booking-quote]').hidden = false;
      $('[data-booking-continue]').disabled = false;
    }

    async function requestComparison(data, payload) {
      const box = $('[data-booking-comparison]');
      if (!box || !root.dataset.comparisonEndpoint || root.dataset.showParity === 'false') return;
      const t = task('comparison');
      $('[data-comparison-title]').textContent = 'Comparing your stay…';
      $('[data-comparison-copy]').textContent = 'Your direct price is checked. Looking up the channel comparison.';
      const rows = $('[data-comparison-prices]'); rows.hidden = true; rows.replaceChildren();
      try {
        const result = await fetchJSON(root.dataset.comparisonEndpoint, { arrival:payload.arrival, departure:payload.departure, adults:payload.adults, children:payload.children, direct_total:data.total, base_rent:data.base_rent, occupancy_adjustment:data.occupancy_adjustment || 0 }, t);
        if (!current('comparison',t) || fingerprint(payload) !== fingerprint(state.payload)) return;
        const channels = (Array.isArray(result.channels) ? result.channels : []).filter(c => amount(c.total));
        const addRow = (label,total,meta,url='') => {
          const row = node('div','book-york-booking__comparison-row'); const left = node('div'); left.append(node('strong','',label),node('small','',meta));
          const href = safeLink(url); if (href) { const link = node('a','book-york-booking__comparison-link',`Check ${label} ↗`); link.href = href; link.target = '_blank'; link.rel = 'noopener noreferrer'; left.append(link); }
          row.append(left,node('span','',money(total,data.currency || 'GBP'))); rows.append(row);
        };
        addRow('Book York',data.total,'Live direct total');
        channels.forEach(c => addRow(c.label || 'Booking channel',c.total,`${c.source === 'observed' ? 'Observed checkout price' : 'Channel price estimate'}${c.scenario && c.scenario !== 'Public' ? ` · ${c.scenario}` : ''}`,c.url));
        rows.hidden = !channels.length;
        $('[data-comparison-title]').textContent = channels.length ? 'Your direct price, compared' : 'Your live direct price is ready';
        $('[data-comparison-copy]').textContent = channels.length ? 'Compare the totals below. Estimates can differ from the final channel checkout price.' : 'No channel comparison is available for this stay. Your direct price is still ready.';
        $('[data-comparison-meta]').textContent = `${nightText(nights(payload.arrival,payload.departure))} · your selected guests and dates`;
        $('[data-comparison-methodology]').textContent = `${result.methodology || 'Your Book York total is checked live in OwnerRez. Channel estimates use the current pricing settings, rather than a guaranteed checkout price.'} ${result.disclaimer || 'Channel rates may change.'}`;
      } catch (e) {
        if (!current('comparison',t)) return;
        $('[data-comparison-title]').textContent = 'Your live direct price is ready';
        $('[data-comparison-copy]').textContent = 'Channel comparison is temporarily unavailable. You can still book using your checked direct price.';
      }
    }

    async function requestQuote() {
      if (state.checkoutBusy) return;
      if (!state.confirmed) { open('guests'); return; }
      if (!state.arrival || !state.departure) { open(state.arrival ? 'departure' : 'arrival'); return; }
      invalidateQuote(); setError('');
      const t = task('quote'), payload = payloadFor(state.departure), key = fingerprint(payload);
      state.busy = true; root.setAttribute('aria-busy','true');
      status('Checking your live price and stay rules…','loading'); updateSummary(); updateFooter(); updateGuidance();
      try {
        const data = await fetchJSON(root.dataset.quoteEndpoint,{...payload,include_booking_terms:true},t);
        if (!current('quote',t) || key !== fingerprint(payloadFor(state.departure))) return;
        if (!amount(data.total) || Number(data.total) < 0) throw new Error('The booking service did not return a complete price. Please try again.');
        money(data.total,data.currency || 'GBP'); // Validate currency before showing a payable total.
        state.quote = data; state.payload = payload; state.busy = false;
        renderQuote(data,payload); updateSummary(); updateFooter();
        status(`Live price checked for ${party()} and ${nightText(nights(payload.arrival,payload.departure))}.`,'success');
        emit('bookyork:booking-quote',{payload,quote:data});
        const wasOpen = dialog.open;
        if (wasOpen) { closeDialog(false); $('[data-price-heading]').focus({preventScroll:true}); $('[data-price-heading]').scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',block:'start'}); }
        requestComparison(data,payload);
      } catch (e) {
        if (!current('quote',t)) return;
        state.busy = false;
        const rule = ruleFrom(e.data);
        const stayLength = nights(payload.arrival,payload.departure);
        status('Your dates are saved. See the guidance to continue.','waiting');
        if (rule.code === 'minimum_stay' && rule.value) {
          state.departure = '';
          rules.earliest = '';
          const context = `Your ${nightText(stayLength)} selection needs at least ${nightText(rule.value)} for those dates.`;
          setError(`${context} Choose a longer stay; this is not a message that the month is fully booked.`);
          setView('departure');
          // This is a rule for the attempted interval. Do not overwrite an arrival-only minimum.
          checkRules({startAt:rule.value,context,learnBase:false});
        } else if (['arrival_restricted','departure_restricted','unavailable','maximum_stay','occupancy'].includes(rule.code)) {
          state.departure = '';
          if (rule.code === 'departure_restricted') restrictedDepartures.add(payload.departure);
          rules.status = 'error'; rules.earliest = ''; rules.code = rule.code; rules.message = rule.message || 'These dates need adjusting. Choose another departure or change your arrival.';
          setError(rules.message); setView('departure');
          if (rule.code === 'occupancy') { state.draftAdults = state.adults; state.draftChildren = state.children; }
        } else {
          setError(e.message || 'Unable to check your price. Please try again.');
          rules.status = 'error'; rules.code = ''; rules.message = 'The price check did not finish. Your dates are saved. Select See price to retry.';
          $('[data-booking-retry]').hidden = false;
          updateGuidance();
        }
        updateSummary(); updateFooter(); renderCalendar();
      } finally { if (current('quote',t)) { root.removeAttribute('aria-busy'); state.busy = false; updateFooter(); } }
    }

    listen($('[data-guest-trigger]'),'click',e => open('guests',e.currentTarget));
    $$('[data-open-calendar]').forEach(b => listen(b,'click',() => open(b.dataset.openCalendar,b)));
    listen($('[data-booking-start]'),'click',e => { if (state.arrival && state.departure && state.confirmed && !state.busy) requestQuote(); else open(!state.confirmed ? 'guests' : state.arrival ? 'departure' : 'arrival',e.currentTarget); });
    listen($('[data-edit-guests]'),'click',() => open('guests'));
    listen($('[data-edit-arrival]'),'click',() => open('arrival'));
    listen($('[data-edit-departure]'),'click',() => open('departure'));
    listen($('[data-rule-guests]'),'click',() => open('guests'));
    listen($('[data-rule-arrival]'),'click',() => open('arrival'));
    $$('[data-guest-step]').forEach(b => listen(b,'click',() => {
      const field = b.dataset.guestStep === 'adults' ? 'draftAdults' : 'draftChildren';
      const lower = field === 'draftAdults' ? 1 : 0;
      const upper = capacity || (field === 'draftAdults' ? 10 : 8);
      const delta = Number(b.dataset.direction);
      if (delta > 0 && capacity && state.draftAdults + state.draftChildren >= capacity) return;
      state[field] = Math.max(lower,Math.min(upper,state[field] + delta));
      updateDraft();
    }));
    listen($('[data-guest-done]'),'click',async () => {
      if (state.view !== 'guests') {
        if (!availability) { await ensureAvailability(true); return; }
        if (state.view !== 'departure' || state.busy) return;
        if (state.departure) { requestQuote(); return; }
        return;
      }
      const changed = !state.confirmed || state.adults !== state.draftAdults || state.children !== state.draftChildren;
      if (changed) { invalidateQuote(); resetRules(); }
      state.adults = state.draftAdults; state.children = state.draftChildren; state.confirmed = true;
      setError(''); updateSummary();
      const next = state.arrival && state.departure ? 'departure' : pendingView === 'departure' && state.arrival ? 'departure' : 'arrival';
      setView(next); $('[data-dialog-title]').focus({preventScroll:true});
      status('Guests confirmed. Choose your dates to see your exact price.');
      const ready = await ensureAvailability();
      if (ready && dialog.open && state.view !== 'guests') {
        if (state.view === 'departure' && !state.departure) checkRules();
      }
    });
    listen($('[data-dialog-close]'),'click',() => closeDialog());
    listen(dialog,'cancel',e => { e.preventDefault(); closeDialog(); });
    listen(dialog,'close',unlockScroll);
    listen(dialog,'click',e => { if (e.target === dialog) { const r = dialog.getBoundingClientRect(); if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) closeDialog(); } });
    listen($('[data-dialog-back]'),'click',() => { invalidateQuote(); abort('rules'); if (rules.status === 'loading') rules.status = 'idle'; open(state.view === 'departure' ? 'arrival' : 'guests'); });
    listen($('[data-clear-dates]'),'click',() => { invalidateQuote(); resetRules(); state.arrival = ''; state.departure = ''; setError(''); setView('arrival'); status('Dates cleared. Your guests are still confirmed.'); });
    listen($('[data-calendar-grid]'),'click',e => { const b = e.target.closest('[data-date]'); if (b) chooseDate(b.dataset.date); });
    listen($('[data-rule-retry]'),'click',() => checkRules());
    listen($('[data-availability-retry]'),'click',async () => { if (await ensureAvailability(true)) { if (state.arrival && state.view === 'departure') checkRules(); } });
    listen($('[data-calendar-prev]'),'click',() => { displayMonth = shiftMonth(displayMonth,-1); renderCalendar(); });
    listen($('[data-calendar-next]'),'click',() => { displayMonth = shiftMonth(displayMonth,1); renderCalendar(); });
    listen($('[data-month-jump]'),'change',e => { if (parse(e.target.value)) { displayMonth = e.target.value; renderCalendar(); } });
    listen($('[data-calendar-grid]'),'keydown',e => {
      const b = e.target.closest('[data-date]'); if (!b) return;
      const d = b.dataset.date; let next;
      if (e.key === 'ArrowLeft') next = add(d,-1);
      if (e.key === 'ArrowRight') next = add(d,1);
      if (e.key === 'ArrowUp') next = add(d,-7);
      if (e.key === 'ArrowDown') next = add(d,7);
      if (e.key === 'Home') next = add(d,-((parse(d).getUTCDay()+6)%7));
      if (e.key === 'End') next = add(d,6-((parse(d).getUTCDay()+6)%7));
      if (e.key === 'PageUp') next = shiftDateMonth(d,e.shiftKey ? -12 : -1);
      if (e.key === 'PageDown') next = shiftDateMonth(d,e.shiftKey ? 12 : 1);
      if (!next) return;
      e.preventDefault(); next = next < today() ? today() : next > to ? to : next;
      if (!$(`[data-date="${next}"]`)) visibleMonthFor(next);
      renderCalendar(next);
    });
    listen(dialog,'keydown',e => {
      if (e.key !== 'Tab') return;
      const list = [...dialog.querySelectorAll('button:not(:disabled),select,input,a[href],summary,[tabindex="0"]')].filter(el => el.tabIndex >= 0 && el.getClientRects().length);
      if (!list.length) return;
      const first = list[0], last = list[list.length-1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === $('[data-dialog-title]'))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    listen($('[data-calendar-grid]'),'pointerover',e => {
      const b = e.target.closest('[data-date]');
      if (!b || e.pointerType !== 'mouse' || state.view !== 'departure' || state.departure || state.busy || b.dataset.reason) return;
      const d = b.dataset.date;
      $$('[data-date]').forEach(day => day.classList.toggle('is-range-preview',day.dataset.date > state.arrival && day.dataset.date < d));
      // Preview is visual only. The summary always reflects deliberate selections.
    });
    listen($('[data-calendar-grid]'),'pointerleave',() => { $$('[data-date]').forEach(d => d.classList.remove('is-range-preview')); updateSummary(); });
    listen(form,'submit',e => { e.preventDefault(); requestQuote(); });
    listen($('[data-booking-retry]'),'click',requestQuote);
    listen($('[data-discount-code]'),'input',() => {
      invalidateQuote();
      if (state.confirmed && state.arrival && state.departure) { status('Updating your discount code…','waiting'); discountTimer = setTimeout(requestQuote,650); }
      updateSummary();
    });
    listen($('[data-booking-continue]'),'click',async () => {
      if (state.checkoutBusy) return;
      if (!state.quote || !state.payload || fingerprint(state.payload) !== fingerprint(payloadFor(state.departure))) { setError('Please check your stay price again before continuing.'); return; }
      const fields = [$('[data-guest-first]'),$('[data-guest-last]'),$('[data-guest-email]')];
      const bad = fields.find(input => !input.value.trim() || !input.checkValidity());
      if (bad) { setError('Please enter your first name, last name and a valid email address.'); bad.focus(); bad.reportValidity(); return; }
      setError(''); state.checkoutBusy = true;
      const editing = [...new Set([...form.querySelectorAll('button,input,select'), ...fields])].map(el => [el,el.disabled]);
      editing.forEach(([el]) => { el.disabled = true; });
      const releaseEditing = () => { editing.forEach(([el,disabled]) => { el.disabled = disabled; }); state.checkoutBusy = false; };
      const button = $('[data-booking-continue]'); button.disabled = true; button.textContent = 'Preparing secure booking…';
      const t = task('checkout');
      try {
        const result = await fetchJSON(root.dataset.checkoutEndpoint,{...state.payload,
          require_terms_review:true,review_fingerprint:state.quote.review_fingerprint || '',
          first_name:fields[0].value.trim(),last_name:fields[1].value.trim(),email:fields[2].value.trim()},t);
        if (!current('checkout',t)) return;
        if (result.requires_review === true) {
          const fresh = result.quote;
          if (fresh?.ok !== true || !amount(fresh.total) || Number(fresh.total) < 0 || !fresh.review_fingerprint) throw new Error('Your stay needs a fresh price check. Please select See price again.');
          money(fresh.total,fresh.currency || 'GBP');
          state.quote = fresh;
          renderQuote(fresh,state.payload); requestComparison(fresh,state.payload);
          releaseEditing(); button.disabled = false; button.textContent = 'Continue to secure booking →';
          const notice = $('[data-booking-review-notice]');
          notice.textContent = 'Your price or booking terms have been refreshed. Please review them, then continue when you are ready. No booking has been created.';
          notice.hidden = false;
          status('Review the refreshed price and terms before continuing.','waiting');
          $('[data-price-heading]').focus({preventScroll:true});
          $('[data-price-heading]').scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',block:'start'});
          return;
        }
        const href = safeLink(result.payment_form);
        if (!href) throw new Error('The booking service did not return a secure checkout address. Please contact us.');
        window.location.assign(href);
      } catch (e) {
        if (!current('checkout',t)) return;
        setError(`${e.message || 'Secure booking did not open.'} No retry has been sent automatically. If you are unsure whether it went through, contact us before trying again.`);
        releaseEditing(); button.disabled = false; button.textContent = 'Continue to secure booking →';
      }
    });
    const focusNext = e => {
      e?.preventDefault();
      if (state.quote) { root.scrollIntoView({behavior:'smooth',block:'start'}); $('[data-guest-first]').focus({preventScroll:true}); }
      else open(!state.confirmed ? 'guests' : state.arrival ? 'departure' : 'arrival',e?.currentTarget);
    };
    const section = root.dataset.bookingSectionId || '';
    const matches = attr => [...document.querySelectorAll(`[${attr}]`)].filter(el => el.getAttribute(attr) === section);
    if (section) matches('data-book-york-booking-jump').forEach(b => listen(b,'click',focusNext));
    const sticky = section ? matches('data-book-york-mobile-booking-cta')[0] : null;
    const returnCTA = section ? matches('data-book-york-booking-return')[0] : null;
    if (sticky && 'IntersectionObserver' in window) {
      let visible = true, endVisible = false;
      const refresh = () => { sticky.hidden = !window.matchMedia('(max-width:749px)').matches || visible || endVisible || dialog.open; };
      observer = new IntersectionObserver(entries => { entries.forEach(e => { if (e.target === root) visible = e.isIntersecting; if (e.target === returnCTA) endVisible = e.isIntersecting; }); refresh(); },{threshold:.08});
      observer.observe(root); if (returnCTA) observer.observe(returnCTA); listen(window,'resize',refresh,{passive:true}); listen(dialog,'close',refresh); refresh();
    }
    const refreshTermsDay = () => {
      const effective = state.quote?.booking_terms?.effective_date;
      if (!document.hidden && !dialog.open && !state.checkoutBusy && !state.busy && effective && effective !== today()) requestQuote();
    };
    listen(document,'visibilitychange',refreshTermsDay);
    listen(window,'focus',refreshTermsDay);
    const termsClock = window.setInterval(refreshTermsDay,60000);
    let lastMobile = window.matchMedia('(max-width:719px)').matches;
    listen(window,'resize',() => { const mobile = window.matchMedia('(max-width:719px)').matches; if (mobile !== lastMobile) { lastMobile = mobile; if (dialog.open && state.view !== 'guests') renderCalendar(); } },{passive:true});
    const destroy = () => { clearInterval(termsClock); disposed = true; Object.keys(slots).forEach(abort); clearTimeout(discountTimer); life.abort(); observer?.disconnect(); if (dialog.open) dialog.close(); unlockScroll(); instances.delete(root); };
    instances.set(root,{destroy}); root.dataset.bookingBound = '1';
    populateMonths(); updateSummary(); updateDraft(); updateFooter();
  }
  const initAll = (scope = document) => { if (scope.matches?.(SELECTOR)) init(scope); scope.querySelectorAll(SELECTOR).forEach(init); };
  window.BookYorkBookingV2 = { version:'2.2.3',initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => initAll(),{once:true}); else initAll();
  document.addEventListener('shopify:section:load',e => initAll(e.target));
  document.addEventListener('shopify:section:unload',e => { if (e.target.matches?.(SELECTOR)) instances.get(e.target)?.destroy(); e.target.querySelectorAll(SELECTOR).forEach(root => instances.get(root)?.destroy()); });
})();
